import Chart, { useChart } from 'src/components/chart';
import { Box, Stack, Typography, Skeleton } from '@mui/material';
import { useLocales } from 'src/locales';
import { fDate } from 'src/utils/format-time';
import CiCard from './CiCard';
import CustomTooltip from './CustomTooltip';

const SOLO_BIDDER_CHART_COLOR = '#C09910';

export interface SoloBidderWeeklyDatum {
  week_start: string;
  solo_bidder: number | null;
  isSynthetic?: boolean;
}

interface SoloBidderRateCardProps {
  title: string;
  dateRange: string;
  /** Current value as percentage (e.g. 12 for "12%") */
  value?: number | null;
  weeklySeries?: SoloBidderWeeklyDatum[];
  /** Weekly time series for the line chart */
  loading?: boolean;
  /** When true, render only header + content (non-brand structure) for use inside non-brand card wrapper + footer */
  contentOnly?: boolean;
}

export default function SoloBidderRateCard({
  title,
  dateRange,
  value,
  weeklySeries = [],
  loading = false,
  contentOnly = false,
}: SoloBidderRateCardProps) {
  const { t } = useLocales();


  const MAX_WEEKS = 12;
  const hasSinglePoint = weeklySeries.length === 1;

  const chartWeeklySeries: SoloBidderWeeklyDatum[] =
    hasSinglePoint
      ? [
        {
          week_start: `synthetic-${weeklySeries[0].week_start}`,
          solo_bidder: 0,
          isSynthetic: true,
        },
        {
          ...weeklySeries[0],
          isSynthetic: false,
        },
        ...Array.from(
          { length: MAX_WEEKS - 2 },
          (_, index): SoloBidderWeeklyDatum => ({
            week_start: `empty-${index}`,
            solo_bidder: null,
          })
        ),
      ]
      : weeklySeries;

  const displayValue =
    value != null && !Number.isNaN(value)
      ? `${Math.round(value)}%`
      : undefined;

  const hasWeeklyValues = weeklySeries.some(
    (d) => Number(d.solo_bidder) > 0
  );


  const hasDisplayValue =
    value != null &&
    Number.isFinite(Number(value)) &&
    Number(value) > 0;

  const hasNoData = !hasWeeklyValues && !hasDisplayValue;

  // Same chart styling as non-brand visibility: area chart with gradient fill (lighter shadow)
  const chartOptions = useChart({
    chart: {
      type: 'area',
      toolbar: { show: false },
    },
    stroke: {
      width: 2,
      curve: 'smooth',
    },
    markers: {
      size: 0,
      strokeWidth: 0,
      discrete: hasSinglePoint
        ? [
          {
            seriesIndex: 0,
            dataPointIndex: 1,
            fillColor: SOLO_BIDDER_CHART_COLOR,
            strokeColor: SOLO_BIDDER_CHART_COLOR,
            size: 4,
          },
        ]
        : [],
      hover: {
        size: 0,
        sizeOffset: 0,
      },
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
        stops: [0, 100],
      },
    },
    colors: [SOLO_BIDDER_CHART_COLOR],
    xaxis: {
      crosshairs: {
        show: false,
      },
      type: 'category',
      categories: chartWeeklySeries.map((d) => d.week_start),
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: { show: false },
    },
    grid: {
      show: false,
      padding: { left: 12, right: 12, top: 12, bottom: 12 },
    },
    legend: { show: false },
    tooltip: {
      enabled: true,
      x: { show: false, formatter: () => '' },
      shared: true,
      intersect: false,
      custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
        const point = chartWeeklySeries[dataPointIndex];

        if (
          !point ||
          point.isSynthetic ||
          point.solo_bidder == null
        ) {
          return '';
        }

        const formattedDate = point.week_start
          ? fDate(point.week_start, 'dd MMM yyyy')
          : '';

        const pct =
          Math.round((point.solo_bidder ?? 0) * 10) / 10;

        return `
          <div
            style="
              width: 320px;
              box-sizing: border-box;
              border-radius: 8px;
              background: #FFFFFF;
              box-shadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
              font-family: 'Public Sans', sans-serif;
            "
          >
            <div
              style="
                display: flex;
                padding: 14px 16px 0 12px;

                align-items: center;
                justify-content: space-between;
                gap: 12px;
                margin-bottom: 12px;
              "
            >
              <span
                style="
                  color: #333C47;
                  font-family: 'Public Sans', sans-serif;
                  font-size: 14px;
                  font-style: normal;
                  font-weight: 500;
                  line-height: 100%;
                  white-space: nowrap;
                "
              >
                ${title}
              </span>

              <span
                style="
                  color: #637381;
                  text-align: right;
                  font-family: 'Public Sans', sans-serif;
                  font-size: 14px;
                  font-style: normal;
                  font-weight: 400;
                  line-height: 100%;
                  white-space: nowrap;
                "
              >
                ${formattedDate}
              </span>
            </div>

            <div
              style="
                padding: 0 16px 16px 16px;
                color: #637381;
                font-variant-numeric: lining-nums tabular-nums;
                font-family: 'Public Sans', sans-serif;
                font-size: 14px;
                font-style: normal;
                font-weight: 500;
                line-height: 22px;
              "
            >
              ${pct}%
            </div>
          </div>
        `;
      },
    },
  });

  const series = [
    {
      name: title,
      data: chartWeeklySeries.map(
        (d) => Math.round((d.solo_bidder ?? 0) * 10) / 10
      ),
    },
  ];
  // Same header pattern as non-brand visibility: indicator line + label (no value in title; value is topMetric on right)
  const titleWithIndicator = (
    <Stack className="card-top-header-item" direction="row" alignItems="center" spacing={1}>
      <Typography
        component="span"
        sx={{
          fontSize: 14,
          fontWeight: 400,
          color: '#333C47',
          lineHeight: 1,
          fontFamily: 'Public Sans, sans-serif',
        }}
      >
        {title}
      </Typography>
    </Stack>
  );

  function mainContent() {
    if (hasNoData) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 120,
            marginY: '30px',
            color: 'text.secondary',
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
            {t('dashboard.global.no_data_available')}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '12px', mt: 1 }}>
            {t('dashboard.global.visibility.no_results_found_description')}
          </Typography>
        </Box>
      );
    }

    if (weeklySeries.length === 0) {
      return <Box sx={{ width: '100%', height: 70 }} />;
    }

    return (
      <Chart
        type="area"
        series={series}
        options={chartOptions}
        sx={{ width: '100%', height: 70 }}
        height={70}
      />
    );
  }

  const soloBidderRateTooltip = t('dashboard.global.visibility.tooltips.soloBidderRate');

  if (contentOnly) {
    const chartBody =
      hasNoData ? (
        <Stack height="100%" justifyContent="center" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.global.no_data_available')}
          </Typography>
        </Stack>
      ) : weeklySeries.length === 0 ? (
        <Box sx={{ width: '100%', height: '100%' }} />
      ) : (
        <Chart
          type="area"
          series={series}
          options={chartOptions}
          sx={{
            width: '100%',
            height: '100%',
          }}
          height="100%"
        />
      );

    return (
      <>
        <Stack
          className="card-top-header"
          borderBottom={1}
          borderColor="divider"
          direction="row"
          px={2}
          height={40}
          minHeight={40}
          maxHeight={40}
          boxSizing="border-box"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack
            className="card-top-header-item"
            direction="row"
            alignItems="center"
            spacing={1}
          >
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              {titleWithIndicator}
            </Box>

            <CustomTooltip title={soloBidderRateTooltip} placement="top">
              <Box
                sx={{
                  cursor: 'help',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M6 8V6M6 4H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z"
                    stroke="#637381"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Box>
            </CustomTooltip>

            <Typography
              className="card-top-header-item-value"
              variant="h6"
              color="text.primary"
            >
              {displayValue}
            </Typography>
          </Stack>
        </Stack>

        {loading ? (
          <Skeleton
            variant="rectangular"
            width="100%"
            height={127}
          />
        ) : (
          <Stack
            width="100%"
            height={127}
            minHeight={127}
            maxHeight={127}
            flex="0 0 127px"
            sx={{
              overflow: 'hidden',
            }}
          >
            {chartBody}
          </Stack>
        )}
      </>
    );
  }

  return (
    <CiCard
      dateRange={dateRange}
      title={titleWithIndicator}
      titleTooltip={soloBidderRateTooltip}
      topMetric={displayValue}
      mainContent={mainContent()}
      loading={loading}
    />
  );
}
